import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Link as LinkIcon, ArrowLeft, CheckCircle, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Textarea, Card, Logo } from '@/components/ui';

const ReportAbuse = () => {
  const [email, setEmail] = useState('');
  const [link, setLink] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const { t } = useTranslation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email || !link || !message) {
      setError(t('reportAbuse.requiredFields'));
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(
        'https://us-central1-klipr-it.cloudfunctions.net/reportAbuse',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, link, message }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to send report');
      }

      setIsSuccess(true);
    } catch {
      setError(t('reportAbuse.error'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-900 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <Logo size="md" />
        </Link>

        <Card variant="elevated" padding="lg">
          {isSuccess ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <h1 className="text-2xl font-bold text-dark-800 dark:text-white mb-2">{t('reportAbuse.successTitle')}</h1>
              <p className="text-gray-500 dark:text-gray-400 mb-6">
                {t('reportAbuse.successMessage')}
              </p>
              <Button
                variant="ghost"
                size="md"
                onClick={() => {
                  setIsSuccess(false);
                  setEmail('');
                  setLink('');
                  setMessage('');
                }}
              >
                {t('reportAbuse.sendAnother')}
              </Button>
            </div>
          ) : (
            <>
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                  <AlertTriangle className="w-8 h-8 text-orange-600 dark:text-orange-400" />
                </div>
                <h1 className="text-2xl font-bold text-dark-800 dark:text-white mb-2">{t('reportAbuse.title')}</h1>
                <p className="text-gray-500 dark:text-gray-400">
                  {t('reportAbuse.subtitle')}
                </p>
              </div>

              {error && (
                <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl">
                  <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  label={t('reportAbuse.emailLabel')}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('reportAbuse.emailPlaceholder')}
                  leftIcon={<Mail className="w-5 h-5" />}
                  required
                  autoComplete="email"
                />

                <Input
                  label={t('reportAbuse.linkLabel')}
                  type="url"
                  value={link}
                  onChange={(e) => setLink(e.target.value)}
                  placeholder={t('reportAbuse.linkPlaceholder')}
                  leftIcon={<LinkIcon className="w-5 h-5" />}
                  required
                />

                <Textarea
                  label={t('reportAbuse.messageLabel')}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t('reportAbuse.messagePlaceholder')}
                  required
                  rows={4}
                />

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  className="w-full"
                  isLoading={isLoading}
                >
                  {t('reportAbuse.submit')}
                </Button>
              </form>
            </>
          )}
        </Card>

        <Link
          to="/"
          className="mt-6 flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400 hover:text-dark-800 dark:hover:text-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('notFound.backToHome')}
        </Link>
      </div>
    </div>
  );
};

export default ReportAbuse;
